
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Property } from '@/types/property';
import { logError, handleAsyncError } from '@/utils/errorUtils';

export const useProperties = () => {
  return useQuery({
    queryKey: ['properties'],
    queryFn: async (): Promise<Property[]> => {
      console.log('🏠 Fetching properties from Supabase...');
      
      const result = await handleAsyncError(async () => {
        const { data: properties, error } = await supabase
          .from('properties')
          .select(`
            *,
            hosts (*),
            neighborhoods (*),
            property_highlights (*),
            reviews (*)
          `)
          .eq('available', true);

        if (error) {
          console.error('Supabase error:', error);
          throw error;
        }

        return properties;
      }, 'Properties fetch');

      if (!result) {
        console.log('No properties found or error occurred');
        return [];
      }

      console.log(`✅ Fetched ${result.length} properties from Supabase`);

      // Transform Supabase data to match Property interface
      return result.map(property => ({
        id: property.id,
        title: property.title,
        shortDescription: property.short_description,
        longDescription: property.long_description,
        price: property.price,
        rating: property.rating,
        guests: property.guests,
        images: property.images || [],
        bedrooms: property.bedrooms,
        bathrooms: property.bathrooms,
        area: property.area || 0,
        available: property.available,
        latitude: property.latitude,
        longitude: property.longitude,
        shortName: property.short_name,
        featured: property.featured,
        airbnbUrl: property.airbnb_url,
        propertyHighlights: property.property_highlights?.map(h => ({
          title: h.title,
          description: h.description,
          icon: h.icon
        })) || [],
        host: property.hosts ? {
          name: property.hosts.name,
          avatar: property.hosts.avatar || '',
          superHost: property.hosts.super_host || false,
          experience: property.hosts.experience || ''
        } : undefined,
        neighborhood: property.neighborhoods ? {
          city: property.neighborhoods.city,
          state: property.neighborhoods.state,
          country: property.neighborhoods.country,
          description: property.neighborhoods.description || '',
          longDescription: property.neighborhoods.long_description || ''
        } : undefined,
        reviews: property.reviews?.map(r => ({
          id: r.id,
          name: r.name,
          rating: r.rating,
          comment: r.comment,
          location: r.location || '',
          date: r.date
        })) || []
      }));
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      console.log(`Query failed (attempt ${failureCount}):`, error);
      return failureCount < 3;
    },
  });
};

export const useProperty = (propertyId: string) => {
  return useQuery({
    queryKey: ['property', propertyId],
    queryFn: async (): Promise<Property | null> => {
      console.log(`🏠 Fetching property ${propertyId} from Supabase...`);
      
      const result = await handleAsyncError(async () => {
        const { data: property, error } = await supabase
          .from('properties')
          .select(`
            *,
            hosts (*),
            neighborhoods (*),
            property_highlights (*),
            reviews (*)
          `)
          .eq('id', propertyId)
          .single();

        if (error) {
          throw error;
        }

        return property;
      }, `Property ${propertyId} fetch`);

      if (!result) {
        console.log('Property not found or error occurred');
        return null;
      }

      console.log(`✅ Fetched property ${propertyId} from Supabase:`, result);
      console.log(`🔍 Raw host_id:`, result.host_id);
      console.log(`🔍 Raw neighborhood_id:`, result.neighborhood_id);

      // Transform Supabase data to match Property interface
      const transformedProperty = {
        id: result.id,
        title: result.title,
        shortDescription: result.short_description,
        longDescription: result.long_description,
        price: result.price,
        rating: result.rating,
        guests: result.guests,
        images: result.images || [],
        bedrooms: result.bedrooms,
        bathrooms: result.bathrooms,
        area: result.area || 0,
        available: result.available,
        latitude: result.latitude,
        longitude: result.longitude,
        shortName: result.short_name,
        featured: result.featured,
        airbnbUrl: result.airbnb_url,
        // Store the raw IDs for access in the edit form
        hostId: result.host_id,
        neighborhoodId: result.neighborhood_id,
        propertyHighlights: result.property_highlights?.map(h => ({
          title: h.title,
          description: h.description,
          icon: h.icon
        })) || [],
        host: result.hosts ? {
          name: result.hosts.name,
          avatar: result.hosts.avatar || '',
          superHost: result.hosts.super_host || false,
          experience: result.hosts.experience || ''
        } : undefined,
        neighborhood: result.neighborhoods ? {
          city: result.neighborhoods.city,
          state: result.neighborhoods.state,
          country: result.neighborhoods.country,
          description: result.neighborhoods.description || '',
          longDescription: result.neighborhoods.long_description || ''
        } : undefined,
        reviews: result.reviews?.map(r => ({
          id: r.id,
          name: r.name,
          rating: r.rating,
          comment: r.comment,
          location: r.location || '',
          date: r.date
        })) || []
      };

      return transformedProperty as Property;
    },
    enabled: !!propertyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};
